import { useState, useEffect, type MouseEvent } from 'react';

const navItems = [
  { label: '사업개요', href: '#overview' },
  { label: '브랜드', href: '#brand' },
  { label: '입지환경', href: '#environment' },
  { label: '프리미엄', href: '#premium' },
  { label: '단지안내', href: '#complex' },
  { label: '세대안내', href: '#unit' },
  { label: '오시는길', href: '#location' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = () => setMobileOpen(false);

  const handleLogoClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between section-padding h-16 md:h-20">
        {/* Logo */}
        <a href="#top" className="flex flex-col items-start shrink-0 cursor-pointer" onClick={handleLogoClick}>
          <span className={`font-display text-lg md:text-xl font-semibold tracking-wider transition-colors duration-300 ${
            scrolled ? 'text-primary-500' : 'text-foreground-50'
          }`}>
            AUVERSONE
          </span>
          <span className={`text-[10px] md:text-xs tracking-widest transition-colors duration-300 ${
            scrolled ? 'text-foreground-400' : 'text-foreground-200'
          }`}>
            헤이리 오베르원
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={handleNavClick}
              className={`px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors duration-300 rounded-md hover:bg-background-200/50 cursor-pointer ${
                scrolled
                  ? 'text-foreground-200 hover:text-foreground-50'
                  : 'text-foreground-100 hover:text-foreground-50'
              }`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* Right Side */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href="#register"
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border transition-all duration-300 whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'border-primary-500/50 text-primary-400 hover:bg-primary-500/10 hover:text-primary-300'
                : 'border-foreground-300/30 text-foreground-200 hover:bg-foreground-50/10 hover:text-foreground-50'
            }`}
          >
            <i className="ri-user-add-line"></i>
            관심고객등록
          </a>
          <a
            href="tel:1668-1451"
            className={`flex items-center gap-2 text-sm font-medium whitespace-nowrap transition-colors duration-300 cursor-pointer ${
              scrolled ? 'text-foreground-200 hover:text-primary-400' : 'text-foreground-100 hover:text-foreground-50'
            }`}
          >
            <i className="ri-phone-line"></i>
            <span className="font-display text-base">1668-1451</span>
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="lg:hidden p-2 rounded-md transition-colors cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="메뉴 열기"
        >
          <i className="ri-menu-3-line text-xl text-foreground-50"></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/30 max-h-[80vh] overflow-y-auto">
          <nav className="flex flex-col py-4 section-padding">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={handleNavClick}
                className="block py-3 text-foreground-200 hover:text-foreground-50 font-medium border-b border-background-200/20 last:border-0 cursor-pointer"
              >
                {item.label}
              </a>
            ))}
            <div className="flex flex-col gap-3 pt-4 mt-2 border-t border-background-200/20">
              <a
                href="#register"
                onClick={handleNavClick}
                className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-full border border-primary-500/50 text-primary-400 cursor-pointer"
              >
                <i className="ri-user-add-line"></i>
                관심고객등록
              </a>
              <a
                href="tel:1668-1451"
                className="flex items-center justify-center gap-2 text-sm font-medium text-foreground-200 cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                1668-1451
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}